import {
  autoUpdate,
  flip,
  offset,
  Placement,
  ReferenceType,
  size,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useListNavigation,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import React, { ForwardedRef } from 'react';

type useComboBoxProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  placement?: Placement;
  listRef: React.MutableRefObject<(HTMLElement | null)[]>;
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
  forwardedRef: ForwardedRef<HTMLInputElement>;
};

export const useComboBox = ({
  isOpen,
  setIsOpen,
  placement = 'bottom-start',
  listRef,
  activeIndex,
  setActiveIndex,
  forwardedRef,
}: useComboBoxProps) => {
  const nodeId = useFloatingNodeId();

  const { context, refs, floatingStyles } = useFloating<ReferenceType>({
    nodeId,
    placement,
    // Ensure floating element remains anchored to the reference element
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: setIsOpen,
    // Functions to apply to floating element
    middleware: [
      offset(2), // Gap between floating and reference element
      flip({ padding: 10 }), // Change placement of the floating element to keep it in view.
      size({
        apply({ rects, availableHeight, availableWidth, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`, // Same size as reference element
            maxWidth: `${availableWidth}px`, // Ensure menu not wider than width of viewport
            maxHeight: `${availableHeight}px`, // Ensure menu not taller than height of viewport
          });
        },
      }),
    ],
  });

  const mergedReferenceRefs = useMergeRefs([forwardedRef, refs.setReference]);

  // Automatically generate screen reader props for reference and floating element
  const role = useRole(context, { role: 'listbox' });
  // Closes the floating element when a dismissal is requested
  const dismiss = useDismiss(context);
  // Adds arrow key-based navigation of a list of items
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
    allowEscape: true,
  });

  // Merge/compose interaction event handlers together
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  } = useInteractions([role, dismiss, listNavigation]);

  return {
    nodeId,
    context,
    refs,
    floatingStyles,
    mergedReferenceRefs,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
  };
};
